import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Box,
  Button,
  Grid,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { compact, map } from 'lodash';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import useRoles from 'common/hooks/useRoles';
import useTabContext from 'common/hooks/useTabContext';
import { normalizePhoneNumber } from 'common/utils';
import { ShowDefaultPartnerContact } from 'common/utils/featureFlags';

import choosePatientContactForPartner from '../../utils/choosePatientContactForPartner';
import DefaultContactChip from '../DefaultContactChip';
import PartnerContactsDialog from '../PartnerContacts/PartnerContactsDialog';
import PlaceholderEmailMarker from '../PartnerContacts/PlaceholderEmailMarker';

export const CONTACT_DETAILS_TAB_VALUE = 'PatientContactDetailsTab';

const COLUMNS = [
  { title: 'Partner', width: '30%' },
  { title: 'Patient email', width: '45%' },
  { title: 'Patient phone', width: '25%' },
];

const SKELETON_ROWS = [1, 2, 3];

const ContactCell = ({ value, contactKind, isDefaultShown, valueAdornment }) => (
  <TableCell>
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1 }}>
      <Typography
        variant='body2'
        sx={{ wordBreak: 'break-word', color: value ? 'text.primary' : 'text.secondary' }}
      >
        {value || '—'}
      </Typography>
      {isDefaultShown && <DefaultContactChip contactKind={contactKind} />}
      {value && valueAdornment}
    </Box>
  </TableCell>
);

ContactCell.propTypes = {
  value: PropTypes.string,
  contactKind: PropTypes.oneOf(['email', 'phone']).isRequired,
  isDefaultShown: PropTypes.bool.isRequired,
  valueAdornment: PropTypes.node,
};

ContactCell.defaultProps = { value: undefined, valueAdornment: null };

const PatientContactDetailsTab = () => {
  const { mpi } = useParams();
  const hasAccess = useRoles();
  const isActiveTab = useTabContext().tab === CONTACT_DETAILS_TAB_VALUE;
  const patient = useSelector(({ patient: patientsByMpi }) => patientsByMpi[mpi]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (!isActiveTab) {
    return null;
  }

  // The first name must exist; without it the patient is still loading.
  const isPatientLoaded = !!patient?.firstName;
  const partnerContacts = patient?.contactsFromPartners || [];
  const canEditPartnerContacts =
    hasAccess.editPartnerContact && isPatientLoaded && partnerContacts.length > 0;

  const renderRows = () => {
    if (!isPatientLoaded) {
      return map(SKELETON_ROWS, (skeletonRow) => (
        <TableRow key={skeletonRow} data-testid='contact-details-skeleton-row'>
          {map(COLUMNS, ({ title }) => (
            <TableCell key={title}>
              <Skeleton width='60%' />
            </TableCell>
          ))}
        </TableRow>
      ));
    }

    if (!partnerContacts.length) {
      return (
        <TableRow>
          <TableCell colSpan={COLUMNS.length}>
            <Typography variant='body2' color='text.secondary'>
              No partners
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    return map(partnerContacts, (partnerContact) => {
      const patientContact = choosePatientContactForPartner({
        patient,
        partnerContact,
        isDefaultContactShown: ShowDefaultPartnerContact,
      });

      return (
        <TableRow key={partnerContact.partnerId}>
          <TableCell component='th' scope='row'>
            {partnerContact.partnerName}
          </TableCell>
          <ContactCell
            value={patientContact.email}
            contactKind='email'
            isDefaultShown={patientContact.isDefaultEmailShown}
            valueAdornment={patientContact.isPlaceholderEmailShown && <PlaceholderEmailMarker />}
          />
          <ContactCell
            value={normalizePhoneNumber(patientContact.phoneNumber)}
            contactKind='phone'
            isDefaultShown={patientContact.isDefaultPhoneShown}
          />
        </TableRow>
      );
    });
  };

  return (
    <>
      <Grid container direction='column' spacing={2}>
        <Grid item container justifyContent='space-between' alignItems='flex-start' wrap='nowrap'>
          <Box>
            <Typography variant='h6' component='h2'>
              Contact Details
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              The email and phone each partner uses to notify this patient.
            </Typography>
          </Box>
          {canEditPartnerContacts && (
            <Button
              variant='outlined'
              size='small'
              startIcon={<EditOutlinedIcon />}
              onClick={() => setIsDialogOpen(true)}
              sx={{ flexShrink: 0, ml: 2 }}
            >
              Edit partner contacts
            </Button>
          )}
        </Grid>

        <Grid item>
          <TableContainer>
            <Table aria-label='Patient contact by partner' size='small'>
              <TableHead>
                <TableRow>
                  {map(COLUMNS, ({ title, width }) => (
                    <TableCell key={title} sx={{ width }}>
                      <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>{renderRows()}</TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {isDialogOpen && (
        <PartnerContactsDialog
          mpi={mpi}
          patientName={compact([patient?.firstName, patient?.lastName]).join(' ')}
          partnerContacts={partnerContacts}
          onClose={() => setIsDialogOpen(false)}
        />
      )}
    </>
  );
};

export default PatientContactDetailsTab;
